import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import ToastMe from '@/components/ui/ToastMe';
import { API_BASE_URL } from '@/constants/ApiConstant';
import validationErrors from '@/lib/validationErrors';

const ENDPOINT = `${API_BASE_URL}/leave-request`;
const ADMIN_LIST_API = `${API_BASE_URL}/admin-user/data`;

const LEAVE_TYPES = ['annual', 'sick', 'maternity', 'training', 'other'];
const leaveTypeOptions = LEAVE_TYPES.map((t) => ({
  value: t,
  label: t[0].toUpperCase() + t.slice(1),
}));

// Coerce many shapes -> string id
const normalizeId = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    if (v._id) return String(v._id);
    try {
      return String(v);
    } catch {
      return '';
    }
  }
  return String(v);
};

export default function AddLeaveDrawer({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) {
  const { userData: user } = useSelector((s) => s.Auth);

  const isHR = !!(user?.isSuperAdmin || user?.permissions?.includes('view_leave_all'));
  const myEmpId = normalizeId(user?._id);

  /** Build headers (JSON + Authorization + tenant) */
  const authHdr = useMemo(() => {
    const tok =
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      '';

    const rawCompany =
      localStorage.getItem('X-Company-Id') ||
      user?.company_id?._id ||
      user?.company_id ||
      '';

    const rawBranch =
      localStorage.getItem('X-Branch-Id') ||
      localStorage.getItem('branch_id') ||
      user?.default_branch_id ||
      '';

    const h = { 'Content-Type': 'application/json' };
    if (tok) h.Authorization = `Bearer ${tok}`;

    const companyId = normalizeId(rawCompany);
    const branchId = normalizeId(rawBranch);

    if (companyId) h['X-Company-Id'] = companyId;
    if (branchId && branchId !== 'all' && branchId !== '*') h['X-Branch-Id'] = branchId;

    return h;
  }, [user]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      employee: '',
      leaveType: '',
      startDate: '',
      endDate: '',
      reason: '',
      status: 0, // 0=pending, 1=rejected, 2=approved
    },
  });

  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (!activeDrawer) return;

    // Reset form each time drawer opens
    reset({
      employee: '',
      leaveType: '',
      startDate: '',
      endDate: '',
      reason: '',
      status: 0,
    });

    if (isHR) {
      fetchEmployees();
    } else {
      setValue('employee', myEmpId); // self
    }

    if (editDataId) fetchById(editDataId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer, editDataId]);

  async function fetchEmployees() {
    try {
      // Determine current branch and normalize to string
      const rawBranch =
        localStorage.getItem('X-Branch-Id') ||
        localStorage.getItem('branch_id') ||
        user?.default_branch_id ||
        null;

      const currentBranch = normalizeId(rawBranch);

      // Build explicit extra_filter for server-side filtering
      const extra_filter = { status: 1 }; // active employees
      if (currentBranch && currentBranch !== 'all' && currentBranch !== '*') {
        extra_filter.branch_id = currentBranch; // << MUST be string
      }

      const r = await fetch(ADMIN_LIST_API, {
        method: 'POST',
        headers: authHdr,
        body: JSON.stringify({
          page: 1,
          limit: 1000,
          extra_filter,
        }),
      }).then((r) => r.json());

      if (r.success) {
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        // Defensive client-side prune (backstop)
        const pruned =
          currentBranch && currentBranch !== 'all' && currentBranch !== '*'
            ? list.filter((e) => {
                const d = normalizeId(e.default_branch_id);
                const arr = Array.isArray(e.branch_ids) ? e.branch_ids : [];
                return d === currentBranch || arr.some((b) => normalizeId(b) === currentBranch);
              })
            : list;

        setEmployees(pruned);
      } else {
        setEmployees([]);
      }
    } catch (e) {
      console.error(e);
      setEmployees([]);
    }
  }

  async function fetchById(id) {
    setBusy(true);
    try {
      const r = await fetch(`${ENDPOINT}/${id}`, { headers: authHdr }).then((r) => r.json());
      if (r.success) {
        const d = r.data || {};
        setValue('employee', normalizeId(d.employee?._id || d.employee));
        setValue('leaveType', d.leaveType || '');
        setValue('startDate', String(d.startDate || '').slice(0, 10));
        setValue('endDate', String(d.endDate || '').slice(0, 10));
        setValue('reason', d.reason || '');
        setValue('status', typeof d.status === 'number' ? d.status : 0);
      }
    } finally {
      setBusy(false);
    }
  }

  const close = () => {
    reset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  async function onSubmit(data) {
    setBusy(true);
    try {
      const payload = { ...data };

      if (!isHR) payload.employee = myEmpId; // force self on non-HR

      const r = await fetch(editDataId ? `${ENDPOINT}/${editDataId}` : ENDPOINT, {
        method: editDataId ? 'PUT' : 'POST',
        headers: authHdr,
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (!r.success) {
        validationErrors(r.errorData, r.message);
        return;
      }

      ToastMe(editDataId ? 'Leave updated.' : 'Leave submitted.', 'success');
      close();
    } catch (err) {
      ToastMe(err?.message || 'Something went wrong', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={close}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Employee */}
          <Controller
            control={control}
            name="employee"
            rules={{ required: 'Employee is required' }}
            render={({ field }) =>
              isHR ? (
                <InputSelect
                  {...field}
                  labelText="Employee"
                  options={employees.map((e) => ({
                    value: normalizeId(e._id),
                    label: e.name,
                  }))}
                  errorType={errors.employee?.type}
                  onChange={(o) => field.onChange(o?.value || '')}
                  value={
                    employees
                      .map((e) => ({ value: normalizeId(e._id), label: e.name }))
                      .find((o) => o.value === field.value) || null
                  }
                />
              ) : (
                <Input
                  {...field}
                  size="sm"
                  labelText="Employee"
                  readOnly
                  value={user?.name || 'Me'}
                  labelStyleClassName="db-field-title"
                />
              )
            }
          />

          {/* Leave Type */}
          <Controller
            control={control}
            name="leaveType"
            rules={{ required: 'Leave type is required' }}
            render={({ field }) => (
              <InputSelect
                {...field}
                labelText="Leave Type"
                options={leaveTypeOptions}
                errorType={errors.leaveType?.type}
                onChange={(o) => field.onChange(o?.value || '')}
                value={leaveTypeOptions.find((o) => o.value === field.value) || null}
              />
            )}
          />

          {/* Dates */}
          <Controller
            control={control}
            name="startDate"
            rules={{ required: 'Start date is required' }}
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Start Date"
                type="date"
                labelStyleClassName="db-field-title required"
                errorData={{ required: 'Start date is required' }}
                errorType={errors.startDate?.type}
              />
            )}
          />
          <Controller
            control={control}
            name="endDate"
            rules={{ required: 'End date is required' }}
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="End Date"
                type="date"
                labelStyleClassName="db-field-title required"
                errorData={{ required: 'End date is required' }}
                errorType={errors.endDate?.type}
              />
            )}
          />

          {/* Reason */}
          <Controller
            control={control}
            name="reason"
            rules={{ required: 'Reason is required' }}
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Reason"
                type="text"
                labelStyleClassName="db-field-title required"
                errorData={{ required: 'Reason is required' }}
                errorType={errors.reason?.type}
              />
            )}
          />

          {/* Status controls for HR when editing a pending request */}
          {isHR && editDataId && (
            <div className="col-span-full flex flex-wrap items-center gap-2">
              <span className="db-field-title">Status:</span>

              {/* Approve */}
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <button
                    type="button"
                    className={`db-btn ${field.value === 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-black'}`}
                    onClick={() => field.onChange(2)}
                  >
                    <i className="lab lab-check-line" /> Approve
                  </button>
                )}
              />

              {/* Reject */}
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <button
                    type="button"
                    className={`db-btn ${field.value === 1 ? 'bg-red-600 text-white' : 'bg-gray-200 text-black'}`}
                    onClick={() => field.onChange(1)}
                  >
                    <i className="lab lab-close-line" /> Reject
                  </button>
                )}
              />
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button type="submit" disabled={busy} className="db-btn py-2 text-white bg-primary">
            <i className="lab lab-save" />
            <span>{editDataId ? 'Update' : 'Submit'}</span>
          </button>
          <button type="button" className="modal-btn-outline" onClick={close}>
            <i className="lab lab-close" />
            <span>Close</span>
          </button>
        </div>
      </form>
    </SidebarDrawer>
  );
}
